'use client';

import * as React from 'react';

type Level = 'line' | 'specialist' | 'manager';
type PayModel = 'fixed' | 'percent';

type CalcState = {
  level: Level;
  salary: number;
  model: PayModel;
  val: number;
  count: number;
};

type FormData = {
  name: string;
  phone: string;
  role: string;
  email: string;
  agreed: boolean;
};

const nf = new Intl.NumberFormat('ru-RU');
const cf = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 });

function timeline(level: Level) {
  if (level === 'line') return { first: '3–5 РД', close: '10–20 РД' };
  if (level === 'manager') return { first: '7–10 РД', close: '30–45 РД' };
  return { first: '5–7 РД', close: '20–35 РД' };
}

function useRecruitCalc(defaults: CalcState) {
  const [state, setState] = React.useState<CalcState>(defaults);

  React.useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('recruit_calc') : null;
      if (raw) {
        setState({ ...defaults, ...JSON.parse(raw) });
      }
    } catch (e) {
      console.error('Failed to parse localStorage data:', e);
    }
  }, []);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('recruit_calc', JSON.stringify(state));
    }
  }, [state]);

  const feeOne = state.model === 'fixed' ? state.val : state.salary * (state.val / 100);
  const feeTotal = feeOne * state.count;
  const t = timeline(state.level);

  return { state, setState, feeOne, feeTotal, t };
}

// FIX: Адаптивные отступы (px-4 на мобильных, px-6 на десктопе)
function Section({ id, gray, className, children }: React.PropsWithChildren<{ id?: string; gray?: boolean; className?: string }>) {
  return (
    <section id={id} className={`${gray ? 'bg-slate-50' : ''} py-12 md:py-20 ${className ?? ''}`}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6">{children}</div>
    </section>
  );
}

function Card({ className, children }: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div className={`rounded-2xl bg-white p-4 sm:p-6 shadow-[0_6px_18px_rgba(0,0,0,0.06)] ${className ?? ''}`}>
      {children}
    </div>
  );
}

function KPI({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-3 text-white min-h-[72px]">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/60 text-base font-black text-[#003B9B]">
        {icon}
      </div>
      <div>
        <div className="text-xl font-extrabold leading-none">{value}</div>
        <div className="text-sm opacity-90 leading-tight mt-0.5">{label}</div>
      </div>
    </div>
  );
}

function LabeledInputNumber(props: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  step?: number;
}) {
  const { label, value, onChange, min, step = 1 } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        min={min}
        step={step}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      />
    </label>
  );
}

function LabeledSelect<T extends string>(props: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  const { label, value, onChange, options } = props;
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-600 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-2xl font-extrabold text-[#003B9B] break-words">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </Card>
  );
}

export default function RecruitmentPage() {
  const { state, setState, feeOne, feeTotal, t } = useRecruitCalc({
    level: 'specialist',
    salary: 120_000,
    model: 'fixed',
    val: 60_000,
    count: 2,
  });

  const [formData, setFormData] = React.useState<FormData>({
    name: '',
    phone: '',
    role: '',
    email: '',
    agreed: false,
  });

  const [formStatus, setFormStatus] = React.useState('');

  const handleSubmit = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (!formData.agreed) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.role.trim()) {
      setFormStatus('Пожалуйста, заполните все обязательные поля (Имя, Телефон, Роль, Email) для отправки заявки.');
      return;
    }

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/recruitment-requests`;
    setFormStatus('Отправка...');

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: formData }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', role: '', email: '', agreed: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Неизвестная ошибка сервера.';
        setFormStatus(`Произошла ошибка: ${errorMessage}`);
        console.error('Ошибка API:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка fetch/сети:', error);
    }
  };

  const getStatusColorClass = (status: string) => {
    if (status.includes('Ошибка') || status.includes('сети')) return 'text-red-600';
    if (status.includes('Пожалуйста')) return 'text-amber-600';
    if (status.includes('успешно')) return 'text-green-600';
    return 'text-slate-500';
  };

  return (
    <main className="bg-white text-slate-900 w-full overflow-x-hidden">
      <section className="mt-20 bg-gradient-to-br from-[#00214F] to-[#003B9B] text-white pt-16 pb-16 md:pt-24 md:pb-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black break-words">Подбор персонала</h1>
          <p className="mt-3 max-w-3xl text-base md:text-lg opacity-95">
            Рекрутинг полного цикла под вашу отрасль: бриф → сорсинг → интервью по компетенциям → проверка рекомендаций →
            оффер → онбординг 30/60/90. Первые релевантные кандидаты — <b>за 5–7 рабочих дней</b>.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a href="#calc" className="w-full sm:w-auto inline-flex items-center justify-center bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base shadow-lg">
              Оценить бюджет
            </a>

            <a href="#process" className="w-full sm:w-auto inline-flex items-center justify-center bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base shadow-lg">
              Как мы работаем
            </a>
          </div>

          <div className="mt-7 rounded-2xl border border-white/30 bg-white/10 p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KPI icon={<span>⏰</span>} value="5–7 дней" label="до первых кандидатов" />
              <KPI icon={<span>🎯</span>} value="3–6" label="релевантных в кратком списке" />
              <KPI icon={<span>✅</span>} value="60–180 дн." label="гарантия замены" />
              <KPI icon={<span>📊</span>} value="97 %" label="заявок в SLA" />
            </div>
          </div>
        </div>
      </section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card>
            <h3 className="text-xl font-extrabold">Что берём на себя</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Бриф: профиль, EVP, условия, KPI роли</li>
              <li>Сорсинг и скрининг (телефон/видео)</li>
              <li>Интервью по компетенциям + кейсы</li>
              <li>Проверка рекомендаций / безопасность*</li>
              <li>Оффер и переговоры</li>
              <li>Сопровождение онбординга 30/60/90</li>
            </ul>
            <p className="mt-2 text-xs text-slate-500">* По согласованию и в рамках закона.</p>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Методы и инструменты</h3>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-base text-black">
              <li>Прямой/целевой поиск, логические операторы, рефералы</li>
              <li>HH, GitHub/Stack Overflow (для IT)</li>
              <li>ATS/CRM, отчёты о статусе и воронка найма</li>
              <li>Оценка по компетенциям и навыкам межличностного общения</li>
            </ul>
          </Card>
          <Card>
            <h3 className="text-xl font-extrabold">Право и комплаенс</h3>
            <p className="mt-3 text-base text-black">
              Соблюдаем ТК РФ и 152-ФЗ (персональные данные). Равные возможности без дискриминации. Отчётность и хранение
              данных в рамках политики безопасности.
            </p>
          </Card>
        </div>
      </Section>

      <Section>
        <h2 className="mb-4 text-2xl font-black">Пакеты</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              name: 'Lite',
              items: ['Сорсинг + скрининг', 'Краткий список и сводные комментарии', 'Сопровождение оффера'],
              note: 'Подходит для линейных позиций и массового подбора.',
            },
            {
              name: 'Standard',
              items: ['Всё из Lite', 'Интервью по компетенциям', 'Проверка рекомендаций', 'Онбординг 30/60/90'],
              note: 'Оптимально для специалистов и ведущих.',
            },
            {
              name: 'Pro',
              items: ['Все из стандартного набора', 'Картирование рынка и консультации EVP', 'Расширенная гарантия на 180 дней'],
              note: 'Для менеджеров и редких ролей.',
            },
          ].map((p) => (
            <Card key={p.name}>
              <h3 className="text-xl font-black">{p.name}</h3>
              <ul className="mt-3 list-disc pl-5 space-y-2 text-black">
                {p.items.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">{p.note}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section id="calc" gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Калькулятор подбора</h3>
            <p className="mt-1 text-xs text Slate-500">
              Черновая оценка. Комиссия может считаться как фикс за закрытие или % от оклада.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LabeledSelect<Level>
                label="Уровень позиции"
                value={state.level}
                onChange={(v) => setState({ ...state, level: v })}
                options={[
                  { value: 'line', label: 'Линейный / Массовый' },
                  { value: 'specialist', label: 'Специалист / Ведущий' },
                  { value: 'manager', label: 'Менеджер / Редкая роль' },
                ]}
              />
              <LabeledInputNumber
                label="Оклад, ₽/мес"
                value={state.salary}
                onChange={(v) => setState({ ...state, salary: v })}
                min={0}
              />
              <LabeledSelect<PayModel>
                label="Модель оплаты"
                value={state.model}
                onChange={(v) => setState({ ...state, model: v })}
                options={[
                  { value: 'fixed', label: 'Фикс за закрытие' },
                  { value: 'percent', label: 'Процент от оклада' },
                ]}
              />
              <LabeledInputNumber
                label="Значение (₽ или %)"
                value={state.val}
                onChange={(v) => setState({ ...state, val: v })}
                min={0}
              />
              <LabeledInputNumber
                label="Кол-во вакансий"
                value={state.count}
                onChange={(v) => setState({ ...state, count: v })}
                min={1}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <StatCard label="Комиссия за 1 закрытие" value={cf.format(Math.round(feeOne))} />
              <StatCard label="Итого за все позиции" value={cf.format(Math.round(feeTotal))} />
              <StatCard label="Первые кандидаты" value={t.first} />
              <StatCard label="Оценка сроков закрытия" value={t.close} />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              * Оценка ориентировочная и зависит от рынка, бренда и условий.
            </p>
          </Card>

          <Card>
            <h3 className="text-xl font-extrabold">Соглашение об уровне сервиса (SLA) и гарантии</h3>
            <div className="mt-3 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full min-w-[300px] border-collapse text-base">
                <thead>
                  <tr className="text-left text-black">
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Метрика</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Цель</th>
                    <th className="border-b border-slate-200 px-2 py-2 font-bold">Комментарий</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">Первые резюме</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">≤ 5 РД</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">при согласовании профиля</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">Shortlist</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">7–14 РД</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">3–6 кандидатов</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">Отчётность</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">еженедельно</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">воронка, статусы, риски</td>
                  </tr>
                  <tr>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">Гарантия</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">60/90/180 дн.</td>
                    <td className="border-b border-slate-100 px-2 sm:px-4 py-3">по пакету и уровню</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-slate-500">Замена действует при причинах, зависящих от кандидата.</p>
          </Card>
        </div>
      </Section>

      <Section id="process">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { n: '1. Бриф', d: 'Профиль роли, EVP, условия; согласование SLA и каналов.' },
            { n: '2. Поиск', d: 'Прямой/целевой поиск, картирование рынка, запуск рекламы.' },
            { n: '3. Отбор', d: 'Скрининг, интервью по компетенциям, кейсы, рекомендации.' },
            { n: '4. Оффер', d: 'Переговоры, сопровождение выхода, контроль онбординга.' },
          ].map((s) => (
            <Card key={s.n}>
              <h3 className="text-lg font-extrabold">{s.n}</h3>
              <p className="mt-1 text-base text-black">{s.d}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section gray>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card>
            <h3 className="text-xl font-extrabold">Кейс</h3>
            <p className="mt-2 text-base text-black">
              <b>Задача:</b> 8 инженеров-наладчиков в новый цех (2 смены).
            </p>
            <p className="mt-1 text-base text-black">
              <b>Результат:</b> Лучшие кандидаты за 4 рабочих дня; 12 отобранных кандидатов; 8 предложений за 28 рабочих дней, замены не требуются.
            </p>
          </Card>
          <Card className="rounded-lg shadow-sm p-4">
            <h3 className="text-lg font-extrabold">FAQ</h3>

            <details className="group mt-2 border-t border-slate-200/70 py-3" open>
              <summary className="cursor-pointer text-base font-semibold hover:text-blue-700 transition pr-2"> Работаете ли вы на условиях гонорара за успешный результат? </summary>
              <p className="mt-1 text-base text-slate-700"> Да. Для сложных вакансий также доступен гонорар (30/30/40). </p>
            </details>

            <details className="group border-t border-slate-200/70 py-3">
              <summary className="cursor-pointer text-base font-semibold hover:text-blue-700 transition pr-2"> Где ищете кандидатов? </summary>
              <p className="mt-1 text-base text-slate-700"> Отраслевые сообщества, рекомендации, прямой поиск, базы данных и др. </p>
            </details>

            <details className="group border-t border-slate-200/70 py-3">
              <summary className="cursor-pointer text-base font-semibold hover:text-blue-700 transition pr-2"> Как проверяете компетенции? </summary>
              <p className="mt-1 text-base text-slate-700"> Интервью по компетенциям, кейсы, тестовые задания по роли. </p>
            </details>
          </Card>
        </div>
      </Section>

      <Section id="cta">
        <Card>
          <h3 className="text-xl font-extrabold">Запросить подбор</h3>
          <form className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Имя</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Как к вам обращаться"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Телефон</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="+7 () ___--__-__"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Роль</span>
              <input
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="Позиция / локация / уровень оклада"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-500">Email</span>
              <input
                type="email"
                className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-[#003B9B]"
                placeholder="for@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </label>

            <div className="sm:col-span-2 mt-2">
              <label className="flex items-start text-xs sm:text-sm cursor-pointer text-slate-600">
                <input
                  type="checkbox"
                  checked={formData.agreed}
                  onChange={(e) => setFormData({ ...formData, agreed: e.target.checked })}
                  className="mt-1 mr-2 h-4 w-4 min-w-[16px] rounded border-slate-300 text-[#003B9B] focus:ring-[#003B9B]"
                />
                <span>
                  Я даю <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">согласие на обработку персональных данных</a> и соглашаюсь с <a href="/privacy" className="text-[#003B9B] underline hover:text-[#FF7C22]">Политикой конфиденциальности</a>.
                </span>
              </label>
            </div>
          </form>

          <a
            href="#"
            onClick={handleSubmit}
            className="mt-4 inline-block rounded-xl bg-[#FF7C22] px-6 py-3 text-sm font-bold text-white shadow transition-all duration-200 hover:brightness-[105%] hover:-translate-y-[1px]"
          >
            Отправить
          </a>

          {formStatus && <p className={`mt-4 text-xs ${getStatusColorClass(formStatus)}`}>{formStatus}</p>}
        </Card>
      </Section>
    </main>
  );
}